import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  useLocation,
  Link,
  useParams,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { PlayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import {
  PlayIcon as PlayActiveIcon,
  DocumentIcon as DocumentActiveIcon,
} from '@heroicons/react/24/solid';
import { Typography, Layout, MenuProps, Pagination } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import BreadcrumbContext from '../components/Context';
import DateDropdown from '../components/Header/DateDropdown';
import {
  defaultPageSize,
  filterVideoData,
  filterWhatsTrendingData,
  filterMarketingData
} from '../helper/UtilityMethods';
import ResearchLayout from '../components/Layout/ResarchLayout';
import { getWhatsTrending } from '../services/ApiActions/whatsTrendingAct';
import { getMarketingApis } from '../services/ApiActions/marketingAct';
import ReactMarkdown from 'react-markdown';
import { title } from 'process';
import rehypeRaw from 'rehype-raw';
import DOMpurify from 'dompurify';

const { Title } = Typography;


type MenuItem = Required<MenuProps>['items'][number];

const MarketingPage: React.FC = () => {
  const iconWidth = 17;
  const { addBreadcrumbitem } = useContext(BreadcrumbContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { subTab } = useParams();
  const defaultSelectedMenu = `submenu-${subTab}`;
  const [selectedDateOption, setSelectedDateOption] = useState('all');
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [currentPDFPage, setCurrentPDFPage] = useState(1);
  const [totalPDFPages, setTotalPDFPages] = useState(1);
  //const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const modalEventTimer = useRef<number | null>(null);
  const [brandData, setBrandData] = useState([])
  const [tabsWithCount, setTabsWithCount] = useState([
    {
      label: 'PDF',
      key: 'pdf',
      count: 0,
      icon: <DocumentIcon width={iconWidth} />,
      icon_active: <DocumentActiveIcon width={iconWidth} />,
    },
    {
      label: 'Video',
      key: 'video',
      count: 0,
      icon: <PlayIcon width={iconWidth} />,
      icon_active: <PlayActiveIcon width={iconWidth} />,
    },
  ]);
  const defaultDate =
    searchParams.get('start-date') && searchParams.get('end-date')
      ? [
        dayjs(searchParams.get('start-date')),
        dayjs(searchParams.get('end-date')),
      ]
      : [];
  const [defaultDateRange, setDefaultDateRange] = useState<any>(defaultDate);
  const [selectedTab, setSelectedTab] = useState('pdf');
  const [selectedMenu, setSelectedMenu] = useState(defaultSelectedMenu);
  const [records, setRecords] = useState<any[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState([
    {
      key: 'submenu-why-choose-equentis',
      name: `Why choose Equentis ?`,
      label: (
        <Link
          to={`/marketing/why-choose-equentis`}
          onClick={() => onLinkClick()}
        >
          Why Choose Equentis ?
        </Link>
      ),
    },
    {
      key: 'submenu-myth-busters',
      name: `Myth Busters`,
      label: (
        <Link to={`/marketing/myth-busters`} onClick={() => onLinkClick()}>
          Myth Busters
        </Link>
      ),
    },
    {
      key: 'submenu-calculators',
      name: `Calculators`,
      label: (
        <Link to={`/marketing/calculators`} onClick={() => onLinkClick()}>
          Calculators
        </Link>
      ),
    },
    {
      key: 'submenu-podcast',
      name: `Podcast`,
      label: (
        <Link
          to={`/marketing/podcast`}
          onClick={() => onLinkClick()}
        >
          Podcast
        </Link>
      ),
    },
    {
      key: 'submenu-product-notes',
      name: `Product Notes`,
      label: (
        <Link
          to={`/marketing/product-notes`}
          onClick={() => onLinkClick()}
        >
          Product Notes
        </Link>
      ),
    },
    {
      key: 'submenu-customer-testimonials-pr',
      name: `Customer Testimonials & PR`,
      label: (
        <Link
          to={`/marketing/customer-testimonials-pr`}
          onClick={() => onLinkClick()}
        >
          Customer Testimonials & PR
        </Link>
      ),
    },


  ]);
  useEffect(() => {
    setSelectedTab('pdf');
    setSelectedDateOption('all');
  }, [subTab]);
  useEffect(() => {
    setIsLoading(true);
    const startDate = searchParams.get('start-date') || null || undefined;
    const endDate = searchParams.get('end-date') || null || undefined;
    const defaultDate =
      startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : [];
    setDefaultDateRange(defaultDate);

    if (subTab) {
      // console.log("Testing marketing page api***", subTab)
      getMarketingApis(subTab, startDate, endDate, current, pageSize)
        .then((response: any) => {
          // if (subTab === 'brand-dos-donts') {
          //   setBrandData(response?.data?.content)
          // } else {
          const recordList = filterMarketingData(response, selectedTab);
          const videoLinksCount = response?.data?.meta_data?.video_link;
          const pdfLinksCount = response?.data?.meta_data?.pdf;

          setTabsWithCount(prev =>
            prev.map(tab => ({
              ...tab,
              count:
                tab.key === 'pdf' ? (pdfLinksCount ?? 0) :
                  tab.key === 'video' ? (videoLinksCount ?? 0) :
                    tab.count,
            }))
          );
          setFilteredRecords(recordList);
          setTotal(response.data.pagination.total);
          // }
          setRecords(response);

          setSelectedMenu(`submenu-${subTab}`);
        })
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [subTab, searchParams, current, pageSize]);

  const onLinkClick = () => {
    setSelectedTab('pdf');
    setSelectedDateOption('all');
  };
  const onRangeChange = (e: any) => {
    navigate({
      pathname: location.pathname,
      search: e
        ? `?start-date=${e[0].format('YYYY-MM-DD')}&end-date=${e[1].format(
          'YYYY-MM-DD'
        )}`
        : '',
    });
  };
  const onAllClick = () => {
    setSelectedDateOption('all');
    navigate({
      pathname: location.pathname,
      search: ``,
    });
  };
  const handleOnOptionChange = (value: string) => {
    setSelectedDateOption(value);
    setCurrent(1);
  };
  const onTabClick = (tabId: string) => {
    const recordList = filterWhatsTrendingData(records, tabId);

    setSelectedTab(tabId);
    setFilteredRecords(recordList);
  };
  const handlePageChange = (current: number) => {
    setCurrent(current);
  };
  const handleShowSizeChange = (current: number, size: number) => {
    setPageSize(size);
    setCurrent(current);
  };

  // if(subTab === 'brand-dos-donts'){
  //   return (
  //     <div
  //       style={{
  //         height: '100%',
  //         overflow: 'auto',
  //         paddingRight: '20px',
  //       }}
  //     >
  //       <Title
  //         className="scenario-heading"
  //         level={4}
  //         style={{ marginTop: 0 }}
  //       >
  //         {"Brands Do's & Don'ts"}
  //       </Title>
  //       <div style={{ fontWeight: '500' }}>
  //         <ReactMarkdown rehypePlugins={[rehypeRaw]}>
  //           {/* {contentDetails?.content &&
  //             DOMpurify.sanitize(contentDetails.content)} */}
  //         </ReactMarkdown>
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <>
      <ResearchLayout
        brandsData={brandData}
        showMarkDown={false}
        tabs={tabsWithCount}
        title="Marketing"
        records={filteredRecords}
        object="marketing"
        selectedMenu={selectedMenu}
        menuItems={menuItems}
        isLoading={isLoading}
        defaultDateRange={defaultDateRange}
        onRangeChange={onRangeChange}
        onAllClick={onAllClick}
        selectedTab={selectedTab}
        onTabClick={onTabClick}
        showDateRange={true}
        pagination={
          total > 0 && (
            <Pagination
              pageSize={pageSize}
              onChange={handlePageChange}
              onShowSizeChange={handleShowSizeChange}
              showSizeChanger={true}
              current={current}
              total={total}
              className="fixed-pagination"
              style={{ display: 'flex', justifyContent: 'center' }}
            />
          )
        }
        selectedDateOption={selectedDateOption}
      // dateFilterComponent={
      //   <DateDropdown
      //     selectedOption={selectedDateOption}
      //     onOptionChange={handleOnOptionChange}
      //   />
      // }
      />
    </>
  );
};

export default MarketingPage;
